/* eslint-disable @typescript-eslint/naming-convention */
import { Account, ProductSelectionPresetSearchResult, ProductSelectionResult, PsLangs } from '@brenger/api-client';
import { IconSearchOutline } from '@brenger/react/icons/IconSearchOutline';
import React from 'react';

import { Translate } from 'react-localize-redux-dep-updated';
import '../../../../assets/product-selection/search.scss';
import { trackEvent } from '../../../../utils/eventTracking';
import { useTypedSelector } from '../../../../hooks';
import { useQuery } from '@tanstack/react-query';
import { priceClient } from '../../../../utils/request';
import { CacheKey } from '../../../../types';
import { IconSmile } from '../../../../brenger-shared-ui';
import { SearchListItem } from './SearchListItem';

export const SearchListZeroResults = ({
  attempts,
  selectResult,
  locale,
}: {
  attempts: number;
  selectResult: (result: Partial<ProductSelectionResult> | ProductSelectionPresetSearchResult) => void;
  locale: PsLangs;
}): React.ReactElement => {
  const accountId = useTypedSelector(state => (state.user.userData?.account as Account)?.id);
  const presets = useQuery({
    queryKey: [CacheKey.RETRIEVE_PS_PRESETS, accountId],
    queryFn: () => priceClient.productSelection.searchPresets({ accountId }),
    enabled: !!accountId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  if (presets.isLoading) {
    return (
      <div className="ps-modal--section">
        <IconSmile spinning={true} />
      </div>
    );
  }

  const presetItems = presets.data || [];

  if (presetItems.length > 0) {
    return (
      <>
        <div className="ps-modal--section" style={{ paddingBottom: 0 }}>
          <h6>
            <Translate id={'request_flow.product_selection.presets_zero_results_heading'} />
          </h6>
        </div>
        {presetItems.map((item, i) => {
          return (
            <SearchListItem
              key={i}
              query={''}
              id={item.preset_id}
              result={{
                ...item,
                label: item.title,
              }}
              bordered={i < presetItems.length - 1}
              locale={locale}
              onClick={() => {
                trackEvent({
                  event: 'productSelection',
                  action: 'click',
                  listPosition: i + 1,
                  listLength: presetItems.length,
                  query: '',
                  id: item.preset_id,
                });
                selectResult(item);
              }}
            />
          );
        })}
      </>
    );
  }
  return (
    <>
      <div className="ps-modal--section" style={{ paddingBottom: 0 }}>
        <h6>
          <Translate
            id={'request_flow.product_selection.popular_searches_count'}
            data={{ count: popularItems.length }}
          />
        </h6>
      </div>
      {popularItems.map((item, i) => {
        return (
          <div
            className="ps-modal--section bordered bp cursor-pointer trigger flex items-center"
            key={item.iri}
            onClick={() => {
              trackEvent({
                event: 'productSelection',
                action: 'popular',
                query: '',
                id: item.iri,
                listLength: popularItems.length,
                listPosition: i + 1,
                attempts,
              });
              selectResult(item);
            }}
          >
            <IconSearchOutline
              className="mr-3"
              style={{
                color: '#7C90A8',
                marginBottom: '-0.1em',
              }}
            />
            {item.name[locale]}
          </div>
        );
      })}
    </>
  );
};

// Below translations are copied from pricing, not ideal, but ok for now
const popularItems: ProductSelectionResult[] = [
  {
    id: 'Sofa|3-seater sofa',
    iri: 'ProductGroup#Couches|Sofa|3-seater sofa',
    name: {
      de_DE: 'Dreisitzer-Sofa',
      en_NL: '3-seater sofa',
      nl_BE: '3-zits zetel',
      nl_NL: '3-zits bank',
    },
    detected_material: [],
    property_identifier: null,
  },
  {
    detected_material: [],
    id: 'Bikes|Bike',
    iri: 'ProductGroup#Bikes|Bike',
    name: {
      de_DE: 'Fahrrad',
      en_NL: 'Bike',
      nl_BE: 'Fiets',
      nl_NL: 'Fiets',
    },
    property_identifier: 'Bike',
  },
  {
    detected_material: [],
    id: 'Closets|Dresser',
    iri: 'ProductGroup#Closets|Dresser',
    name: {
      de_DE: 'Kommode',
      en_NL: 'Dresser',
      nl_BE: 'Dressoir',
      nl_NL: 'Dressoir',
    },
    property_identifier: 'Low dresser',
  },
  {
    detected_material: [],
    id: 'Closets|Wardrobe|Double wardrobe',
    iri: 'ProductGroup#Closets|Wardrobe|Double wardrobe',
    name: {
      de_DE: 'Doppelter Kleiderschrank',
      en_NL: 'Double wardrobe',
      nl_BE: 'Kledingkast (2 deuren)',
      nl_NL: 'Kledingkast (2 deuren)',
    },
    property_identifier: 'Double wardrobe',
  },
  {
    detected_material: [],
    id: 'Heavy Household Appliances|Washing machine',
    iri: 'ProductGroup#Heavy Household Appliances|Washing machine',
    name: {
      de_DE: 'Waschmaschine',
      en_NL: 'Washing machine',
      nl_BE: 'Wasmachine',
      nl_NL: 'Wasmachine',
    },
    property_identifier: 'Washing machine',
  },
  {
    detected_material: [],
    id: 'Tables|Dining table|4 person dining table',
    iri: 'ProductGroup#Tables|Dining table|4 person dining table',
    name: {
      de_DE: 'Esstisch 4 Sitzer',
      en_NL: '4 person dining table',
      nl_BE: '4 persoons eettafel',
      nl_NL: '4 persoons eettafel',
    },
    property_identifier: '4 person dining table',
  },
  {
    detected_material: [],
    id: 'Closets|Buffet cabinet',
    iri: 'ProductGroup#Closets|Buffet cabinet',
    name: {
      de_DE: 'Buffet-Schrank',
      en_NL: 'Buffet cabinet',
      nl_BE: 'Buffetkast',
      nl_NL: 'Buffetkast',
    },
    property_identifier: 'Buffet cabinet',
  },
  {
    detected_material: [],
    id: 'Chairs|Armchair',
    iri: 'ProductGroup#Chairs|Armchair',
    name: {
      de_DE: 'Fauteuil',
      en_NL: 'Armchair',
      nl_BE: 'Fauteuil',
      nl_NL: 'Fauteuil',
    },
    property_identifier: 'Armchair',
  },
];
